import { fourFConfig, moodLevel } from "./config";
import { copingStyleLabel, needLabel } from "./schemaFlip";
import { latestIdeas } from "./needs";
import { SECTIONS, sectionStatus } from "./sections";

const mood = (v, note) => `${moodLevel(v)?.label ?? v}, ${v > 0 ? "plus " : v < 0 ? "minus " : ""}${Math.abs(v)}${note ? `. You wrote: ${note}` : ""}`;

const VALUE = {
  postSuds: (s) => `${s.postSuds} out of 10`,
  triggered: (s) => (s.triggered ? "Yes" : "No"),
  moodBefore: (s) => mood(s.moodBefore, s.moodBeforeNote),
  moodNow: (s) => mood(s.moodNow, s.moodNote),
  triggerNote: (s) => s.triggerNote,
  copingMode: (s) => `${s.copingMode}${s.survivalResponse ? `, with a ${fourFConfig(s.survivalResponse)?.label} response` : ""}`,
  schemaState: (s) => s.schemaState,
  needs: (s) => s.needs.map(needLabel).join(", "),
  emaSchemas: (s) => s.emaSchemas.join(", "),
  aiInsight: (s) => s.aiInsight,
  coping: (s) => s.copingMechanisms.join(", "),
  reflectionNotes: (s) => s.reflectionNotes,
};

const MAX = 3900;
const fit = (t) => (t.length <= MAX ? t : `${t.slice(0, t.lastIndexOf(". ", MAX) + 1)} The rest of this entry is shown on screen.`);

export function flipSpeech(entry) {
  const needs = entry.needs?.length ? `Needs: ${entry.needs.map(needLabel).join(", ")}. ` : "";
  const ideas = latestIdeas([], [entry], 8).map((v) => `Ideas for ${v.need}: ${v.ideas.join(", ")}.`).join(" ");
  return fit(needs + (ideas ? `${ideas} ` : "") + entry.flips.map((f) => [
    `Flip the Script: ${f.schema}${f.copingStyle ? `, ${copingStyleLabel(f.copingStyle)}` : ""}.`,
    f.ownScript && `Your script: ${f.ownScript}.`, f.story && `The story: ${f.story}.`, f.facts && `The facts: ${f.facts}.`,
    f.coach && `Flip Coach: ${f.coach}`, f.action && `Your opposite action: ${f.action}${f.done ? ", done" : ""}.`,
  ].filter(Boolean).join(" ")).join(" "));
}

export const entryKind = (s) => (s.path === "emergency" ? "Get help now" : s.path === "morning" ? "Morning check" : s.moodOnly ? "Mood log" : "Check-in");

export function entrySpeech(s, linkedFlip) {
  const when = new Date(s.timestamp).toLocaleString(undefined, { weekday: "long", day: "numeric", month: "long", hour: "numeric", minute: "2-digit" });
  const parts = [`Your ${entryKind(s).toLowerCase()} from ${when}.${s.preSuds != null ? ` Distress at check-in: ${s.preSuds} out of 10.` : ""}`];
  SECTIONS.forEach((sec) => { if (sectionStatus(s, sec) === "done") parts.push(`${sec.label.replace(/\?$/, "")}: ${VALUE[sec.key](s)}.`); });
  if (linkedFlip) parts.push(flipSpeech(linkedFlip));
  return fit(parts.join(" ").replace(/\.\./g, "."));
}

const brief = (s) => {
  const when = new Date(s.timestamp).toLocaleString(undefined, { weekday: "long", day: "numeric", month: "long", hour: "numeric", minute: "2-digit" });
  return [
    `${entryKind(s)}, ${when}.`,
    s.preSuds != null && `Distress ${s.preSuds}${s.postSuds != null ? ` down to ${s.postSuds}` : ""} out of 10.`,
    s.triggerNote && `Triggered by: ${s.triggerNote}.`,
    s.moodBefore != null && `Mood after the trigger: ${moodLevel(s.moodBefore)?.label}.`,
    s.moodNow != null && `Mood ${s.path === "morning" ? "on waking" : "afterwards"}: ${moodLevel(s.moodNow)?.label}${s.moodNote ? `, and you wrote: ${s.moodNote}` : ""}.`,
    s.copingMechanisms?.length && `What helped: ${s.copingMechanisms.join(", ")}.`,
  ].filter(Boolean).join(" ");
};

export function historySpeech(items) {
  const top = items.slice(0, 5);
  if (!top.length) return "";
  const lines = top.map((it) => (it.k === "s" ? brief(it.s) : `Flip the Script, ${new Date(it.f.timestamp).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}: ${it.f.flips.map((x) => x.schema).join(", ")}.`));
  return fit(`Your ${top.length === 1 ? "most recent entry" : `last ${top.length} entries`}. ${lines.join(" ... ")}`);
}
